import api from './api';

// News article interface
export interface NewsArticle {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  content: string;
  imageUrl?: string;
  coverImage?: string;
  author: {
    id: string;
    name: string;
    profileImage?: string;
  };
  categories: Array<{
    id: string;
    name: string;
    slug: string;
    color?: string;
  }>;
  tags: Array<{
    id: string;
    name: string;
    slug: string;
    color?: string;
  }>;
  publishedAt: string;
  createdAt: string;
  updatedAt: string;
  featured?: boolean;
  status?: string;
  readTime?: number;
  metaTitle?: string;
  metaDescription?: string;
  commentCount?: number;
  media?: Array<{
    id: string;
    type: string;
    title?: string;
    url: string;
    thumbnailUrl?: string;
  }>;
  relatedArticles?: NewsArticle[];
}

// Pagination interface
export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    currentPage: number;
    totalPages: number;
    totalItems: number;
    itemsPerPage: number;
  };
}

// News query params interface
export interface NewsQueryParams {
  page?: number;
  limit?: number;
  sortBy?: 'publishedAt' | 'createdAt' | 'title' | 'viewCount';
  sortOrder?: 'asc' | 'desc';
  categoryId?: string;
  categorySlug?: string;
  tag?: string;
  search?: string;
  featured?: boolean;
}

// Category interface
export interface Category {
  id: string;
  name: string;
  slug: string;
  color?: string;
  description?: string;
  articleCount?: number;
}

const newsService = {
  // Get paginated news articles
  getNews: async (params: NewsQueryParams = {}): Promise<PaginatedResponse<NewsArticle>> => {
    const { 
      page = 1, 
      limit = 10, 
      sortBy = 'publishedAt', 
      sortOrder = 'desc',
      featured
    } = params;

    const response = await api.get<PaginatedResponse<NewsArticle>>('news', { 
      params: { 
        page, 
        limit, 
        sortBy, 
        sortOrder,
        ...(featured !== undefined && { featured: String(featured) })
      } 
    });
    return response.data;
  },

  // Get latest news articles
  getLatestNews: async (limit: number = 3): Promise<NewsArticle[]> => {
    const response = await api.get<{ articles: NewsArticle[] }>('news/latest', { 
      params: { limit } 
    });
    return response.data.articles;
  },

  // Get news article by ID or slug
  getNewsById: async (idOrSlug: string): Promise<NewsArticle> => {
    try {
      const response = await api.get<{ article: NewsArticle }>(`news/${idOrSlug}`);
      
      if (!response.data.article) {
        throw new Error('Article not found');
      }
      
      return response.data.article;
    } catch (error: unknown) {
      if ((error as { response?: { status: number } })?.response?.status === 404) {
        throw new Error('Article not found');
      }
      throw error;
    }
  },

  // Get news article by slug
  getNewsBySlug: async (slug: string): Promise<NewsArticle> => {
    try {
      const response = await api.get<{ article: NewsArticle }>(`news/slug/${slug}`);
      
      if (!response.data.article) {
        throw new Error('Article not found');
      }
      
      return response.data.article;
    } catch (error: unknown) {
      if ((error as { response?: { status: number } })?.response?.status === 404) {
        throw new Error('Article not found');
      }
      throw error;
    }
  },

  // Get all news categories
  getNewsCategories: async (): Promise<Category[]> => {
    const response = await api.get<{ categories: Category[] }>('news/categories');
    return response.data.categories;
  },

  // Get news articles by category
  getNewsByCategory: async (categoryIdOrSlug: string, params: Omit<NewsQueryParams, 'categoryId' | 'categorySlug'> = {}): Promise<PaginatedResponse<NewsArticle>> => {
    const { 
      page = 1, 
      limit = 10, 
      sortBy = 'publishedAt', 
      sortOrder = 'desc' 
    } = params;

    const response = await api.get<PaginatedResponse<NewsArticle>>(`news/category/${categoryIdOrSlug}`, {
      params: { page, limit, sortBy, sortOrder }
    });
    return response.data;
  },

  // Search news articles
  searchNews: async (query: string, params: Omit<NewsQueryParams, 'search'> = {}): Promise<PaginatedResponse<NewsArticle>> => {
    const { 
      page = 1, 
      limit = 10, 
      sortBy = 'publishedAt', 
      sortOrder = 'desc',
      categoryId,
      categorySlug,
      tag
    } = params;

    const response = await api.get<PaginatedResponse<NewsArticle>>('news/search', {
      params: { 
        q: query, 
        page, 
        limit, 
        sortBy, 
        sortOrder,
        categoryId,
        categorySlug,
        tag
      }
    });
    return response.data;
  },
};

export default newsService; 